import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  ArrowRight,
  Zap,
  BrainCircuit,
  ShieldCheck
} from 'lucide-react';

const HexagonGridBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);
    resize();

    const hexRadius = 30;
    const hexWidth = Math.sqrt(3) * hexRadius;
    const hexHeight = 2 * hexRadius;
    const xStep = hexWidth;
    const yStep = hexHeight * 0.75;

    const activeHexagons: Array<{c: number; r: number; life: number; color: string}> = [];
    const maxActive = 30;

    const drawHexagon = (x: number, y: number, radius: number, color: string, lineWidth = 1, fill = false) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i + Math.PI / 6;
        const xPos = x + radius * Math.cos(angle);
        const yPos = y + radius * Math.sin(angle);
        if (i === 0) ctx.moveTo(xPos, yPos);
        else ctx.lineTo(xPos, yPos);
      }
      ctx.closePath();
      if (fill) {
        ctx.fillStyle = color;
        ctx.fill();
      } else {
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.stroke();
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const cols = Math.ceil(canvas.width / xStep) + 2;
      const rows = Math.ceil(canvas.height / yStep) + 2;

      if (activeHexagons.length < maxActive && Math.random() > 0.9) {
        activeHexagons.push({
          c: Math.floor(Math.random() * cols),
          r: Math.floor(Math.random() * rows),
          life: 1.0,
          color: Math.random() > 0.5 ? '#06b6d4' : '#8b5cf6'
        });
      }

      for (let r = -1; r < rows; r++) {
        for (let c = -1; c < cols; c++) {
          const xOffset = (r % 2 === 0) ? 0 : xStep / 2;
          const x = c * xStep + xOffset;
          const y = r * yStep;

          const dx = x - mouseRef.current.x;
          const dy = y - mouseRef.current.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const isNearMouse = dist < 200;

          let alpha = 0.05;
          let strokeColor = `rgba(100, 116, 139, ${alpha})`;

          if (isNearMouse) {
            const glow = 1 - (dist / 200);
            strokeColor = `rgba(6, 182, 212, ${0.1 + glow * 0.4})`;
          }

          drawHexagon(x, y, hexRadius - 2, strokeColor);

          const activeIndex = activeHexagons.findIndex(h => h.c === c && h.r === r);
          if (activeIndex !== -1) {
            const activeHex = activeHexagons[activeIndex];
            const fillAlpha = activeHex.life * 0.3;
            drawHexagon(x, y, hexRadius - 2, activeHex.color + Math.floor(fillAlpha * 255).toString(16).padStart(2,'0'), 0, true);

            activeHex.life -= 0.01;
            if (activeHex.life <= 0) activeHexagons.splice(activeIndex, 1);
          }
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 pointer-events-none"
      style={{ background: 'radial-gradient(circle at 50% 50%, #1a1f2e 0%, #0B0F19 100%)' }}
    />
  );
};

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')

    if (authToken && user) {
      const userData = JSON.parse(user)
      if (userData.needs_onboarding) {
        navigate('/onboarding')
      } else {
        navigate('/dashboard')
      }
    }
  }, [navigate])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleSignup = () => {
    navigate('/signup');
  };

  return (
    <div className="min-h-screen text-white font-sans selection:bg-indigo-500/30 overflow-x-hidden relative">
      <HexagonGridBackground />

      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0 mix-blend-screen">
        <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-cyan-600/10 rounded-full blur-[150px]" />
      </div>

      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-[#0B0F19]/80 backdrop-blur-lg border-b border-slate-800' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <Sparkles size={16} className="text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight text-white">Accounting AI</span>
          </div>

          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-300">
            <a href="#features" className="hover:text-cyan-400 transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-cyan-400 transition-colors">How it Works</a>
            <a href="#pricing" className="hover:text-cyan-400 transition-colors">Pricing</a>
          </div>

          <button
            onClick={handleLogin}
            className="px-5 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-medium shadow-[0_0_20px_rgba(8,145,178,0.3)] transition-all hover:scale-105 active:scale-95 border border-cyan-400/20"
          >
            Login
          </button>
        </div>
      </nav>

      <section className="relative pt-32 pb-20 px-6 z-10">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-950/30 border border-cyan-500/30 text-cyan-400 text-xs font-medium animate-in fade-in slide-in-from-bottom-4 duration-700 backdrop-blur-md shadow-[0_0_15px_rgba(6,182,212,0.15)]">
            <Sparkles size={12} />
            <span>New: Generative Invoice Drafting</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight animate-in fade-in slide-in-from-bottom-8 duration-700 delay-100">
            Financial Clarity, <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-indigo-400 to-purple-400">Powered by AI</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
            Automate bookkeeping, generate invoices with natural language, and get real-time financial insights. The future of accounting is here.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-300">
            <button
              onClick={handleSignup}
              className="w-full sm:w-auto px-8 py-4 bg-white text-slate-900 rounded-xl font-bold hover:bg-slate-100 transition-all flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)]"
            >
              Get Started Free <ArrowRight size={18} />
            </button>
            <button className="w-full sm:w-auto px-8 py-4 bg-slate-800/40 border border-slate-600 hover:bg-slate-700/50 text-white rounded-xl font-medium transition-colors backdrop-blur-md">
              Watch Demo
            </button>
          </div>
        </div>
      </section>

      <section id="features" className="py-20 px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-700/50 hover:border-cyan-500/50 transition-all group backdrop-blur-xl shadow-lg hover:shadow-cyan-900/20">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform border border-cyan-500/20">
                <Zap className="text-cyan-400" size={24} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Instant OCR</h3>
              <p className="text-slate-400">Upload receipts and invoices. Our AI extracts data, categorizes expenses, and reconciles instantly.</p>
            </div>
            <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-700/50 hover:border-purple-500/50 transition-all group backdrop-blur-xl shadow-lg hover:shadow-purple-900/20">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform border border-purple-500/20">
                <BrainCircuit className="text-purple-400" size={24} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Smart Insights</h3>
              <p className="text-slate-400">Chat with your finances. Ask &quot;How much did I spend on travel?&quot; and get instant, accurate answers.</p>
            </div>
            <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-700/50 hover:border-indigo-500/50 transition-all group backdrop-blur-xl shadow-lg hover:shadow-indigo-900/20">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform border border-indigo-500/20">
                <ShieldCheck className="text-indigo-400" size={24} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Automated Compliance</h3>
              <p className="text-slate-400">Never worry about tax codes again. We flag potential issues and ensure every document is verified.</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-10 border-t border-slate-800/50 text-center text-slate-500 text-sm relative z-10 bg-[#0B0F19]/80 backdrop-blur-sm">
        <p>© 2024 Accounting AI. All rights reserved.</p>
      </footer>
    </div>
  );
}
